"use client";

import { batch } from "@preact/signals-react";
import { flushSync } from "react-dom";
import {
  LuChartColumn as LuBarChart2,
  LuLayers,
  LuMessagesSquare,
} from "react-icons/lu";
import { startViewTransition } from "use-start-view-transition";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import {
  focusInlineReplyComposer,
  focusIOSKeyboardBridge,
  isIOSDocument,
} from "@/components/app/editor-v3/editor-dom-focus";
import { AddToCollectionDropdown } from "@/components/shared/add-to-collection-dropdown";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { RepostDropdown } from "@/components/shared/repost-dropdown";
import { Button, buttonVariants } from "@/components/ui/button";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { usePostContext } from "@/contexts/post-context";
import { useMobile } from "@/hooks/use-mobile";
import { cn } from "@/utils/lib";
import { ActivityModalType } from "@/utils/post-utils";

import { PostDiamonds } from "../post-activity/post-diamonds";
import { PostReactionSlider } from "../post-reaction-slider";

export const PostCommentActions = ({
  viewsCount = 0,
  creatorPublicKey,
  repostsCount,
  quotedRepostsCount,
}: {
  viewsCount?: number;
  creatorPublicKey: string;
  postCreatorPublicKey: string;
  repostsCount: number;
  quotedRepostsCount: number;
}) => {
  const { postHash, repost, isPureRepost, updatePost, post, postCachePrefix } =
    usePostContext();
  const { postCacheSignal, showCommentEditor, signalMap, postCacheKey } =
    usePostCacheContext(postHash, postCachePrefix);
  const { isMobile } = useMobile();
  const activityPostHash = isPureRepost ? repost?.postHash : post?.postHash;
  const toggleReplyEditor = () => {
    batch(() => {
      // We close all previous editors
      for (const [key, signal] of signalMap.entries()) {
        if (
          (!postCachePrefix || key.includes(postCachePrefix)) &&
          signal.value.showCommentEditor
        ) {
          signal.value = {
            ...signal.value,
            showCommentEditor: false,
          };
        }
      }

      postCacheSignal.value = {
        ...postCacheSignal.value,
        showCommentEditor: !showCommentEditor,
      };
    });
  };

  const renderActions = () => {
    return (
      <div className="flex w-full items-center gap-6">
        <Button
          variant="link"
          className={cn(
            `
              flex items-center gap-2 px-0 text-muted
              hover:text-primary
            `,
            {
              "text-primary": !isMobile && showCommentEditor,
            },
          )}
          size="sm"
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          onClick={() => {
            if (!showCommentEditor && isIOSDocument()) {
              focusIOSKeyboardBridge();
              flushSync(toggleReplyEditor);
              focusInlineReplyComposer(postCacheKey, {
                iosActivation: true,
              });
              window.setTimeout(() => {
                focusInlineReplyComposer(postCacheKey, {
                  iosActivation: true,
                });
              }, 0);
              return;
            }

            startViewTransition(toggleReplyEditor);
          }}
        >
          <LuMessagesSquare />
          <span
            className="
              hidden text-xs
              md:inline
            "
          >
            Reply
          </span>
        </Button>

        <div
          className={cn(
            buttonVariants({
              variant: "link",
            }),
            "flex items-center gap-2 px-0 text-muted",
          )}
          role="button"
        >
          <PostReactionSlider {...{ postHash }} />
        </div>
        <RepostDropdown
          triggerClassName="text-muted"
          updatePost={updatePost}
          post={post}
        >
          <span
            className={cn("text-xs", {
              hidden: repostsCount + quotedRepostsCount === 0,
            })}
          >
            {repostsCount + quotedRepostsCount}
          </span>
        </RepostDropdown>
        <PostDiamonds
          {...{ postHash, creatorPublicKey }}
          triggerClassName="text-muted"
        />
        <AddToCollectionDropdown
          types={["bookmark"]}
          createText="New Bookmark Collection"
          itemData={{
            type: "post",
            postHash,
          }}
        >
          <LuLayers
            className="
              cursor-pointer text-lg text-muted
              hover:text-primary
            "
          />
        </AddToCollectionDropdown>
        <div className="ml-auto flex gap-2 text-right text-xs text-muted">
          <Button
            variant="link"
            className="flex items-center gap-2 px-0 text-xs text-muted"
            onClick={() => {
              if (!activityPostHash) {
                return;
              }

              setDialog(DIALOG.PostActivity, {
                open: true,
                params: {
                  activity: ActivityModalType.view,
                  postHash: activityPostHash,
                },
              });
            }}
          >
            <LuBarChart2 />
            {viewsCount}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <TriggerLoggedInModalWindow>{renderActions()}</TriggerLoggedInModalWindow>
  );
};
